import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import classnames from 'classnames';
import { css, cx } from 'linaria';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { breakpointSmallDesktop, breakpointTinyTablet, breakpointLargeHandset } from '@pocket/web-ui';
import { ModalHeader } from './modal-header';
import { ModalBody } from './modal-body';
import { ModalFooter } from './modal-footer';
import { ModalTabs } from './modal-tabs';
import { CloseButton } from 'components/close-button/close-button';
const MODAL_MAX_WIDTH = 552; // mobile styles pulled out into their own definition so that they can be reused
// by both a media query and css class, which can't be used together as selectors

const modalMobileStyles = `
  left: 0;
  right: 0;
  bottom: 0;
  top: auto;
  border-radius: 0;
  max-height: 100vh;
  max-width: 100%;
  transform-origin: bottom;

  &.animation-base {
    transform: scale(1) translateY(20%);
  }

  &.animation-show {
    transform: scale(1) translateY(0);
    transition-property: opacity, transform;
  }
`;
const modalStyles = "m1gbisw7";
const overlayStyles = "o1ohlj7h";

const Modal = ({
  title,
  appRootSelector,
  screenReaderLabel,
  isOpen,
  onClick,
  forceMobile,
  handleClose,
  shouldCloseOnOverlayClick,
  onAfterOpen,
  onAfterClose,
  modalContentClassName,
  overlayClassName,
  portalClassName,
  showCloseButton,
  doesAnimateTransition,
  children,
  className,
  closeTimeoutMS
}) => {
  // aids in accessibility, http://reactcommunity.org/react-modal/accessibility/
  ReactModal.setAppElement(appRootSelector);
  const [readyAnimate, setReadyAnimate] = useState(false);
  let modalContentRef = useRef(null);
  return <React.Fragment>
      <ReactModal isOpen={isOpen} contentLabel={screenReaderLabel} bodyOpenClassName="modal-open" onRequestClose={() => {
      setReadyAnimate(false);
      handleClose();
    }} onAfterOpen={() => {
      setReadyAnimate(true);
      disableBodyScroll(modalContentRef);
      onAfterOpen();
    }} onAfterClose={() => {
      setReadyAnimate(false);
      if (modalContentRef) enableBodyScroll(modalContentRef);
      clearAllBodyScrollLocks();
      onAfterClose();
    }} shouldCloseOnOverlayClick={shouldCloseOnOverlayClick} className={classnames([className, modalStyles, modalContentClassName, {
      'animation-base': doesAnimateTransition,
      'animation-show': doesAnimateTransition && readyAnimate && isOpen,
      'animation-hide': doesAnimateTransition && readyAnimate && !isOpen,
      'force-mobile': forceMobile
    }])} overlayClassName={classnames([overlayStyles, overlayClassName, {
      'animation-base': doesAnimateTransition,
      'animation-show': doesAnimateTransition && readyAnimate && isOpen,
      'animation-hide': doesAnimateTransition && readyAnimate && !isOpen
    }])} portalClassName={classnames([portalClassName])} closeTimeoutMS={closeTimeoutMS} // necessary to make outgoing animation display
    data-cy="modal" contentRef={node => modalContentRef = node}>
        <div ref={modalContentRef}>
          {
          /** Error on this ref */
        }
          {showCloseButton ? <CloseButton handleClose={handleClose} dataCy="close-modal-button" /> : null}
          {title ? <ModalHeader title={title} /> : null}
          <div onClick={onClick}>{children}</div>
        </div>
      </ReactModal>
    </React.Fragment>;
};

Modal.propTypes = {
  // Pocket Modal Props

  /**
   * title to display in ModalHeader
   */
  title: PropTypes.string,

  /**
   * query selector specifying root element of React App
   */
  appRootSelector: PropTypes.string.isRequired,
  // Exposing React Modal Props

  /**
   * Boolean indicating whether the modal should be open
   */
  isOpen: PropTypes.bool,

  /**
   * Set to true to force the modal into mobile styling.
   */
  forceMobile: PropTypes.bool,

  /**
   * String indicating how the content container should be announced
   to screen readers
   */
  screenReaderLabel: PropTypes.string.isRequired,

  /**
   * Function that will be run when the modal is requested
   to be closed (either by clicking on overlay or pressing ESC).
   Note: It is not called if isOpen is changed by other means.
   */
  handleClose: PropTypes.func.isRequired,

  /**
   * Boolean indicating whether to use a default close button
   */
  showCloseButton: PropTypes.bool,

  /**
   * Boolean indicating whether the modal close when a user clicks
   * on the overlay
   */
  shouldCloseOnOverlayClick: PropTypes.bool,

  /**
   * Function to run after the modal is opened
   */
  onAfterOpen: PropTypes.func,

  /**
   * Function to run after the modal is closed
   */
  onAfterClose: PropTypes.func,

  /**
   * Additional CSS class name to place on the modal content
   */
  modalContentClassName: PropTypes.string,

  /**
   * Additional CSS class name to place on the modal overlay
   */
  overlayClassName: PropTypes.string,

  /**
   * Additional CSS class name to place on the entire portal
   */
  portalClassName: PropTypes.string,

  /**
   * Boolean describing whether the modal should fade in and out
   */
  doesAnimateTransition: PropTypes.bool,

  /**
   * Milliseconds to delay close, useful for outgoing animations
   */
  closeTimeoutMS: PropTypes.number
};
Modal.defaultProps = {
  title: undefined,
  isOpen: false,
  forceMobile: false,
  shouldCloseOnOverlayClick: true,
  onAfterOpen: () => {},
  onAfterClose: () => {},
  modalContentClassName: '',
  overlayClassName: '',
  portalClassName: '',
  showCloseButton: true,
  doesAnimateTransition: true,
  closeTimeoutMS: 75
};
export { Modal, ModalHeader, ModalBody, ModalTabs, ModalFooter };
export default Modal;

require("./modal.linaria.module.css!=!../../../node_modules/linaria/lib/outputCssLoader.js?cacheProvider=!./modal.js");